/**
 * Sign-in with the company identity provider: OpenID Connect, authorization code with PKCE, the
 * flow for a browser app that cannot keep a client secret. Small enough to read in one sitting,
 * which is the point: this is the door to every customer record.
 *
 * The ID token is what the API verifies (issuer, audience = this client id, expiry, signature).
 * Tokens live in sessionStorage: they end with the tab, and a second tab signs in silently with
 * the identity provider's own session. Anything that can run script on this origin could read
 * them wherever they lived, which is why the page loads no third-party script at all.
 */
import { config } from "../config";

const KEY = "tlcrm.oidc";
const PENDING = "tlcrm.oidc.pending";
export const CALLBACK_PATH = "/auth/callback";

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

interface Tokens {
  idToken: string;
  refreshToken: string | null;
  /** Epoch ms the ID token stops being accepted. */
  expiresAt: number;
}

let discovery: Promise<Discovery> | null = null;
function discover(): Promise<Discovery> {
  discovery ??= fetch(
    `${config.oidcIssuer.replace(/\/+$/, "")}/.well-known/openid-configuration`,
  ).then((r) => {
    if (!r.ok) throw new Error(`Identity provider discovery failed (${r.status})`);
    return r.json() as Promise<Discovery>;
  });
  return discovery;
}

const b64url = (bytes: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
const random = (n = 32) => b64url(crypto.getRandomValues(new Uint8Array(n)));
const redirectUri = () => `${location.origin}${CALLBACK_PATH}`;

function read(): Tokens | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function write(tokens: Tokens | null): void {
  try {
    if (tokens) sessionStorage.setItem(KEY, JSON.stringify(tokens));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the session lasts as long as this page does.
  }
  memory = tokens;
}

let memory: Tokens | null = read();

function expiry(idToken: string): number {
  try {
    const payload = JSON.parse(
      atob(idToken.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/")),
    ) as {
      exp?: number;
    };
    return (payload.exp ?? 0) * 1000;
  } catch {
    return 0;
  }
}

export async function beginSignIn(returnTo: string): Promise<never> {
  const d = await discover();
  const verifier = random(48);
  const challenge = b64url(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)),
  );
  const state = random(16);
  sessionStorage.setItem(PENDING, JSON.stringify({ verifier, state, returnTo }));
  const url = new URL(d.authorization_endpoint);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id: config.oidcClientId,
    redirect_uri: redirectUri(),
    scope: "openid email profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  }).toString();
  location.assign(url.toString());
  return new Promise<never>(() => undefined);
}

/** Finish the redirect back from the identity provider. Returns where the person was going. */
export async function completeSignIn(callbackUrl: string): Promise<string> {
  const params = new URL(callbackUrl).searchParams;
  const pending = JSON.parse(sessionStorage.getItem(PENDING) ?? "null") as {
    verifier: string;
    state: string;
    returnTo: string;
  } | null;
  sessionStorage.removeItem(PENDING);
  if (params.get("error"))
    throw new Error(params.get("error_description") ?? "Sign-in was refused.");
  if (!pending || params.get("state") !== pending.state)
    throw new Error("This sign-in link has expired. Start again.");
  const d = await discover();
  const res = await fetch(d.token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code: params.get("code") ?? "",
      redirect_uri: redirectUri(),
      client_id: config.oidcClientId,
      code_verifier: pending.verifier,
    }),
  });
  if (!res.ok) throw new Error("The identity provider did not accept this sign-in.");
  const body = (await res.json()) as { id_token?: string; refresh_token?: string };
  if (!body.id_token) throw new Error("The identity provider sent no ID token.");
  write({
    idToken: body.id_token,
    refreshToken: body.refresh_token ?? null,
    expiresAt: expiry(body.id_token),
  });
  return pending.returnTo || "/";
}

let refreshing: Promise<string | null> | null = null;

/** A current ID token, refreshed a minute before it expires; null when signed out. */
export async function idToken(): Promise<string | null> {
  if (!memory) return null;
  if (memory.expiresAt - Date.now() > 60_000) return memory.idToken;
  if (!memory.refreshToken) return null;
  refreshing ??= (async () => {
    try {
      const d = await discover();
      const res = await fetch(d.token_endpoint, {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: memory!.refreshToken!,
          client_id: config.oidcClientId,
        }),
      });
      if (!res.ok) {
        write(null);
        return null;
      }
      const body = (await res.json()) as { id_token?: string; refresh_token?: string };
      if (!body.id_token) {
        write(null);
        return null;
      }
      write({
        idToken: body.id_token,
        refreshToken: body.refresh_token ?? memory!.refreshToken,
        expiresAt: expiry(body.id_token),
      });
      return body.id_token;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export function hasSession(): boolean {
  return memory !== null;
}

export async function signOut(): Promise<void> {
  const hint = memory?.idToken;
  write(null);
  const d = await discover().catch(() => null);
  if (d?.end_session_endpoint) {
    const url = new URL(d.end_session_endpoint);
    url.search = new URLSearchParams({
      client_id: config.oidcClientId,
      post_logout_redirect_uri: location.origin,
      ...(hint && { id_token_hint: hint }),
    }).toString();
    location.assign(url.toString());
  } else {
    location.assign("/");
  }
}
